import React, { useState, useEffect, useRef } from 'react';
import { Text, ThemePrepared, ChevronDownIcon, Input, Datepicker, Flex } from '@fluentui/react-northstar';
import ToolbarCSS from '../../Toolbar.module.css';
import MgtCss from '../../../../mgt.module.css'
import { NestedFilter } from '../../../../shared/components/NestedFilter/NestedFilter';
import { getDropdownBoxShadow } from '../../../ticketHelper';
import { CustomProperty_TimeInputWrapperClass, FilterDataType, FILTERS } from '../../../../shared/utils/constants';
import { ConvertDateToLocale } from '../../../../shared/common/ConvertDate';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { DatepickerLocalizations } from '../../../../App';
import { formatTimeInput } from '../../../../templates/CustomComponents/Utils/CustomElementUtils';
import { getCardTimeInputISOFormatted } from '../../../../shared/components/TicketTemplateAnswer';
import { themeNames } from '@fluentui/react-teams';
import { CustomTimePicker } from '../../../../custom-properties/CustomTimePicker';

interface CustomViewFilterProps {
  filter: FilterPropsItems;
  globalTheme: ThemePrepared<any>;
  searchValue: string;
  onClick: (item: any, date?: any) => void;
  getFilterCount: () => any;
  onChangeSearchDropdown: (value: any) => void;
  onChangeCollaboratorSearchDropdown: (value: any) => void;
  onChangeSelectedValue: (value: any, propKey: string) => void;
  onTextChange: (e: any, filterId: number) => void;
}

export const CustomViewFilter = ({ filter, globalTheme, searchValue, onClick, getFilterCount, onChangeSearchDropdown, onChangeCollaboratorSearchDropdown, onChangeSelectedValue, onTextChange }: CustomViewFilterProps) => {

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isAssigneeFilter = [FILTERS.ASSIGNEE.title].includes(filter.title);
  const isCollaboratorsFilter = [FILTERS.TicketCollaborators.title].includes(filter.title);
  const isSimpleText = filter.FilterDataType==FilterDataType.String;
  const [isShow, setIsShow] = useState<boolean>(false);
  const {t} = useTranslation();

  const selectedFilterValue = filter.values?.find(val => val.selected);

  const isModifiedDate = [FILTERS.MODIFIED_DATE.title].includes(filter.title);
  let selectedModifiedDate = isModifiedDate ? selectedFilterValue?.date: null;
  const [selectedModifiedDateValue, setSelectedModifiedDateValue] = useState(selectedModifiedDate ? new Date(selectedModifiedDate) : null);

  const isCreatedDate = [FILTERS.CREATED_DATE.title].includes(filter.title);
  let selectedCreatedDate = isCreatedDate ? selectedFilterValue?.date: null;
  const [selectedCreatedDateValue, setSelectedCreatedDateValue] = useState(selectedCreatedDate ? new Date(selectedCreatedDate) : null);

  const isCustomDate = filter.FilterDataType==FilterDataType.Date;
  let selectedCustomDate = isCustomDate ? selectedFilterValue?.date: null;
  const [selectedCustomDateValue, setSelectedCustomDateValue] = useState(selectedCustomDate ? new Date(selectedCustomDate) : null);

  const isCustomTime = filter.FilterDataType==FilterDataType.Time;
  const [_, setSelectedCustomTimeValue] = useState(isCustomTime ? selectedFilterValue?.date: null);

  const isCustomNumber = filter.FilterDataType==FilterDataType.Number;
  let selectedCustomNumber = isCustomNumber ? selectedFilterValue?.number : null;
  const [selectedCustomNumberValue, setSelectedCustomNumberValue] = useState(selectedCustomNumber);

  const [showText, setShowText] = useState(false);
  const inputReference = useRef(null);

  const dropdownClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setIsShow((prev) => { return !prev });
  }

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        setIsShow(true);
        break;
      case 'Escape':
        if (isShow) {
          e.preventDefault();
          e.stopPropagation();
          setIsShow(false);
        }
        break;
      default:
        break;
    }
  }

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsShow(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowDown' && (e.target as HTMLElement)?.tagName === 'INPUT') {
      const firstMenuItem = menuRef.current?.querySelector<HTMLElement>('[role="menuitemcheckbox"]');
      if (firstMenuItem) {
        e.preventDefault();
        firstMenuItem.focus();
      }
    }
  }

  useEffect(() => {
    if (isShow) {
      const firstFocusable = menuRef.current?.querySelector<HTMLElement>('input, [role="menuitemcheckbox"]');
      firstFocusable?.focus();
    }
  }, [isShow]);

  useEffect(() => {
    if(isCustomDate && !selectedCustomDateValue)
      setSelectedCustomDateValue(selectedCustomDate ? new Date(selectedCustomDate) : null);

    if(isCustomNumber && !selectedCustomNumberValue)
      setSelectedCustomNumberValue(selectedCustomNumber);

  }, [filter])

  useEffect(() => {
    function handleClickOutside(event: any) {
      let calenderRef = document.querySelector('.ui-popup__content');
      if (wrapperRef.current && !wrapperRef.current.contains(event.target) && isShow && (calenderRef == null || !calenderRef.contains(event.target))) {
        setIsShow(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [wrapperRef, isShow]);
 
  const customFormatter = date => {
    return ConvertDateToLocale(date, DateTime.DATE_SHORT);
  };
  
  return (
    <>
      {isSimpleText &&
        <div>
          <Text content={<div
            onClick={(e)=>{
              setShowText(true);
              setTimeout(()=>{inputReference.current?.focus();},100);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>)=>{
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowText(true);
                setTimeout(()=>{inputReference.current?.focus();},100);
              }
            }}
            role="button"
            tabIndex={0}
            >{filter.title} <span style={{fontStyle:"italic"}}>{filter.stringValue ? `(${(filter.stringValue.length>10 ? `${filter.stringValue.substring(0,10)}...` : filter.stringValue)})` : ""}</span></div>}
            style={{ 
              backgroundColor: globalTheme.siteVariables.colorScheme.default.background2,
              padding: '8px 12px',
              display: showText ? 'none' : 'block'
             }} 
          />
          <Input
            type="text"
            fluid
            className={ToolbarCSS.filterSearch}
            style={{
              backgroundColor: globalTheme.siteVariables.colorScheme.default.background2,
              padding: '1px 0px', marginTop: "2px",
              display: showText ? 'block' : 'none'
            }}
            value={filter.stringValue}
            onChange={(_,e)=>{
              onTextChange(e,filter.id);
            }}
            inverted
            placeholder={`${t('ticket.child-filter.title')}...`}
            ref={inputReference}
            onBlur={(e)=>{
              setShowText(false);
            }}
          />
        </div>
        
      }
      {!isSimpleText &&
        <div ref={wrapperRef} className={`${ToolbarCSS.filterItem} relative`} key={`${filter.id} ${selectedFilterValue?.key}`}>
          <div
            ref={triggerRef}
            id={`custom-view-filter-trigger-${filter.id}`}
            onClick={(e) => dropdownClick(e)}
            onKeyDown={onTriggerKeyDown}
            role="button"
            tabIndex={0}
            aria-haspopup="menu"
            aria-expanded={isShow}
            style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
            className={`flex justify-between px-3 py-2 cursor-pointer rounded-sm ${ToolbarCSS['custom-view-dropdown']}`}
          >
            <>
              <Text
                style={{ color: globalTheme.siteVariables.colorScheme.default.foreground }}
                content={<span>{`${filter.title} ${getFilterCount()[filter.id] > 0 ? '(' + getFilterCount()[filter.id] + ')' : ''}`}</span>}
              />
              <div className='relative'>
                <div className='absolute top-0 right-0 h-full p-2 z-10'></div>
                <ChevronDownIcon
                  style={{ color: globalTheme.siteVariables.colorScheme.default.foreground }}
                  className={`${ToolbarCSS.filterOptionArrow} ${isShow && ToolbarCSS.filterOptionHidden}`}
                />
              </div>
            </>
          </div>

          {isShow && (
            <div
              ref={menuRef}
              style={{ boxShadow: `${getDropdownBoxShadow(globalTheme)}`, backgroundColor: globalTheme.siteVariables.colorScheme.default.background }}
              className={`origin-top-right min-w-full absolute left-0 rounded-md focus:outline-none max-h-72 overflow-y-scroll z-10 my-2`}
              role='menu'
              aria-orientation='vertical'
              aria-labelledby={`custom-view-filter-trigger-${filter.id}`}
              tabIndex={-1}
              onKeyDown={onMenuKeyDown}
            >
              {isAssigneeFilter &&
                <Input
                  fluid
                  className={ToolbarCSS.filterSearch}
                  style={{
                    backgroundColor: globalTheme.siteVariables.colorScheme.default.background1,
                    padding: '2px 0px', marginTop: "2px",
                  }}
                  value={searchValue}
                  onChange={onChangeSearchDropdown}
                  inverted
                  placeholder={t('ticket.ticket-list.configure-column.filter-by-assignee')}
                />
              }

              {isCollaboratorsFilter &&
                <Input
                  fluid
                  className={ToolbarCSS.filterSearch}
                  style={{
                    backgroundColor: globalTheme.siteVariables.colorScheme.default.background1,
                    padding: '2px 0px', marginTop: "2px",
                  }}
                  value={searchValue}
                  onChange={onChangeCollaboratorSearchDropdown}
                  inverted
                  placeholder={t('ticket.ticket-list.configure-column.filter-by-collaborator')}
                />
              }
              <> 
              <NestedFilter filterItems={filter.values} onClick={(item: any) => onClick(item, selectedCreatedDateValue)} />
              <div className={`${selectedFilterValue ? '' : 'disabledDatePicker'}`}>
                {isModifiedDate && (<Datepicker
                    {...DatepickerLocalizations}
                    disabled={!selectedFilterValue}
                    firstDayOfWeek={0}
                    formatMonthDayYear={customFormatter}
                    className={`${MgtCss.fluidDate} ${MgtCss.inverted}`}
                    onDateChange={(e, p)=>{
                      let selectedDate = p.value === null ? null : p.value.toISOString();
                      onChangeSelectedValue(selectedDate, "date");
                      setSelectedModifiedDateValue(new Date(selectedDate));
                    }}
                    selectedDate={selectedFilterValue?.date ? selectedModifiedDateValue : null}
                  
                  />)}
                {isCreatedDate && (<Datepicker
                    {...DatepickerLocalizations}
                    disabled={!selectedFilterValue}
                    firstDayOfWeek={0}
                    formatMonthDayYear={customFormatter}
                    className={`${MgtCss.fluidDate} ${MgtCss.inverted}`}
                    onDateChange={(e, p)=>{
                      let selectedDate = p.value === null ? null : p.value.toISOString();
                      onChangeSelectedValue(selectedDate, "date");
                      setSelectedCreatedDateValue(new Date(selectedDate));
                    }}
                    selectedDate={selectedFilterValue?.date ? selectedCreatedDateValue : null}
                  />)} 
                {isCustomDate && (<Datepicker
                    {...DatepickerLocalizations}
                    disabled={!selectedFilterValue}
                    firstDayOfWeek={0}
                    formatMonthDayYear={customFormatter}
                    className={`${MgtCss.fluidDate} ${MgtCss.inverted}`}
                    onDateChange={(e, p)=>{
                      let selectedDate = p.value === null ? null : p.value.toISOString();
                      onChangeSelectedValue(selectedDate, "date");
                      setSelectedCustomDateValue(new Date(selectedDate));
                    }}
                    selectedDate={selectedFilterValue?.date ? selectedCustomDateValue : null}
                  />)} 
                  {isCustomNumber && (<Input
                      type="number"
                      fluid
                      disabled={!selectedFilterValue}
                      min={0}
                      defaultValue={selectedFilterValue?.number ? selectedCustomNumberValue : null}
                      onChange={(_, p) => {
                          onChangeSelectedValue(p.value, "number");
                          setSelectedCustomNumberValue(p.value);
                      }}
                      // Matches the ticket list's filter panel, which names the unit because a bare
                      // number is ambiguous for a duration - see Filter.tsx. Every other numeric
                      // filter here is a custom property, where the generic wording is right.
                      placeholder={filter.id == FILTERS.TOTAL_AGENT_TIME.id
                        ? t('ticket.child-filter.total-agent-time-placeholder')
                        : t('ticket.child-filter.type-a-number')}
                    />)}
                </div>
                  {isCustomTime && (
                  <Flex className={CustomProperty_TimeInputWrapperClass(globalTheme.siteVariables.theme === themeNames.Dark )} >
                      <CustomTimePicker
                        disabled={!selectedFilterValue}
                        value={selectedFilterValue?.date ? formatTimeInput(selectedFilterValue?.date) : null}
                        onChange={p => {
                            const timeInput = (p.target as HTMLInputElement).value;
                            let selectedTime = timeInput === null ? null : getCardTimeInputISOFormatted(timeInput);
                            onChangeSelectedValue(selectedTime, "date");
                            setSelectedCustomTimeValue(selectedTime);
                        }}
                      />
                  </Flex>)}                   
                </>
            </div>
          )}
        </div>
      }
      
    </>
  );
};